// client/src/components/pages/LexiStar/StatusBoard/controlRoom.ts
//
// The control room (ST-00253): which group every truck of the status board is in, why, and
// since when — pure, so the control view, its wall display, the phone stack and the table's
// drill-through filter (f=room.<group>[.<sub>]) all count the same trucks.
//
// Groups, in the order a load moves (Bryan's list, ST-00253), then the trucks that need
// attention or are idle:
//   To source · At source (per source site) · Travelling full · At destination (per
//   destination site) · Travelling empty · Delayed (delay zone / stopped en route) · Parked
//   (per geofence, or outside any) · In maintenance (per kind) · Offline / no data ·
//   Unallocated.
//
// Placement, first match wins:
//   1. maintenance — row.maintenance (the server's block), else a maintenance context (the
//      manual override's mode wins over the engine's context). A declared state: it holds even
//      when the tracker is silent (a truck in the workshop often is).
//   1b. a declared delay — row.delay, an open delay event of the maintenance module (ST-00249:
//      "Queue at loader", "Driver unavailable" …): Delayed, one sub-group per code, since the
//      event's start. Declared, so it also holds while the tracker is silent.
//   2. offline — the OFFLINE overlay, no data at all, or the newest data older than
//      OFFLINE_AFTER_S (the engine's own offline threshold, @lexistar/domain STATUS_DEFAULTS).
//   3. delayed — in a delay zone, or stopped / parked (ignition off) while in transit.
//   4. the load cycle by operational context; a context the engine has not derived yet
//      (UNALLOCATED / none) falls back to the active trip's stage — the engine's own precedence
//      ranks the trip stage above "unallocated". At a weighbridge the trip's stage says which
//      side of the load it is on. A DISPATCHED / ACKNOWLEDGED trip that has not moved yet is
//      "To source" (allocated, its next move is to load).
//   5. parked — at a depot / on standby, or stationary without a trip.
//   6. unallocated — moving without a trip, or nothing known.
//
// ST-00249 (the maintenance module's time model): row.maintenance.kind 'planned' / 'breakdown'
// (an open event) splits the maintenance group into Planned maintenance and Breakdown, each with
// its own tone, beside the override's Under maintenance and Out of service; row.delay (an open
// delay event) is placed in Delayed (rule 1b).
//
// ST-00276 phase 2 (map colours & states): a row the server evaluated carries `display` (the
// organisation's display rules); its colour, label and marks then come from there (visualOfRow),
// so every surface draws the truck as the organisation's rules say. The GROUP stays this
// placement (the operational status): a custom rule re-colours the chip, it does not move it.
// A row without `display` (an older server) is resolved here with the default rules, as before.

import type { ComponentType } from 'react';
import {
  FaCarCrash,
  FaDolly,
  FaHourglassHalf,
  FaParking,
  FaQuestionCircle,
  FaRoute,
  FaSatelliteDish,
  FaTruck,
  FaTruckLoading,
  FaTruckMoving,
  FaWrench,
} from 'react-icons/fa';
import { assetMarkerKind } from '../../../map/assetMarker';
import {
  ASSET_STATUS,
  ASSET_STATUS_CLASSES,
  type AssetStatusClasses,
  type AssetStatusKey,
} from '../../../map/assetStatus';
import {
  STAGE_CONTEXT as RULE_STAGE_CONTEXT,
  normaliseMotion,
  resolveAssetVisual,
  type AssetFlag,
  type AssetState,
  type AssetVisual,
} from '../../../map/assetVisualRules';
import { visualOfDisplay } from '../../../map/assetDisplay';
import type { AssetDisplay } from '../../../map/displayRules';
import { TRANSIT_CONTEXTS, isOffline } from './statusLanguage';
import type {
  BoardZone,
  MaintenanceKind,
  StatusBoardRow,
} from '../../../../types/lexistar-status-board';

// ── Groups ──────────────────────────────────────────────────────────────────

export type ControlGroupId =
  | 'to_source'
  | 'at_source'
  | 'loaded'
  | 'at_destination'
  | 'empty'
  | 'delayed'
  | 'parked'
  | 'maintenance'
  | 'offline'
  | 'unallocated';

/** The totals strip's buckets: every truck is in exactly one. */
export type ControlBucket = 'working' | 'idle' | 'maintenance' | 'offline';

/** How a group splits its trucks. */
export type ControlSplit =
  'source_site' | 'destination_site' | 'zone' | 'maintenance' | 'delay' | 'offline';

export interface ControlGroupDef {
  id: ControlGroupId;
  label: string;
  /** One line under the label: what the trucks here are doing. */
  hint: string;
  /** The group's status key (map/assetStatus): its colour on every screen. */
  tone: AssetStatusKey;
  Icon: ComponentType<{ className?: string; size?: number; 'aria-hidden'?: boolean | 'true' }>;
  bucket: ControlBucket;
  /** The load cycle (top row) or the trucks that need a look / stand idle (bottom row). */
  lane: 'cycle' | 'watch';
  split: ControlSplit | null;
}

export const CONTROL_GROUPS: ControlGroupDef[] = [
  {
    id: 'to_source',
    label: 'To source',
    hint: 'Empty, on the way to load',
    tone: 'to_source',
    Icon: FaRoute,
    bucket: 'working',
    lane: 'cycle',
    split: null,
  },
  {
    id: 'at_source',
    label: 'At source',
    hint: 'Loading',
    tone: 'at_source',
    Icon: FaTruckLoading,
    bucket: 'working',
    lane: 'cycle',
    split: 'source_site',
  },
  {
    id: 'loaded',
    label: 'Travelling full',
    hint: 'Loaded, on the way to offload',
    tone: 'loaded',
    Icon: FaTruckMoving,
    bucket: 'working',
    lane: 'cycle',
    split: null,
  },
  {
    id: 'at_destination',
    label: 'At destination',
    hint: 'Offloading',
    tone: 'at_destination',
    Icon: FaDolly,
    bucket: 'working',
    lane: 'cycle',
    split: 'destination_site',
  },
  {
    id: 'empty',
    label: 'Travelling empty',
    hint: 'Offloaded, on the way back',
    tone: 'empty',
    Icon: FaTruck,
    bucket: 'working',
    lane: 'cycle',
    split: null,
  },
  {
    id: 'delayed',
    label: 'Delayed',
    hint: 'In a delay zone or stopped en route',
    tone: 'delay',
    Icon: FaHourglassHalf,
    bucket: 'working',
    lane: 'watch',
    split: 'delay',
  },
  {
    id: 'parked',
    label: 'Parked',
    hint: 'Standing, no trip under way',
    tone: 'parked',
    Icon: FaParking,
    bucket: 'idle',
    lane: 'watch',
    split: 'zone',
  },
  {
    id: 'maintenance',
    label: 'In maintenance',
    hint: 'Maintenance or breakdown, time so far',
    tone: 'maintenance',
    Icon: FaWrench,
    bucket: 'maintenance',
    lane: 'watch',
    split: 'maintenance',
  },
  {
    id: 'offline',
    label: 'Offline / no data',
    hint: 'No position for 90 minutes or more',
    tone: 'offline',
    Icon: FaSatelliteDish,
    bucket: 'offline',
    lane: 'watch',
    split: 'offline',
  },
  {
    id: 'unallocated',
    label: 'Unallocated',
    hint: 'Moving without a trip',
    tone: 'unallocated',
    Icon: FaQuestionCircle,
    bucket: 'idle',
    lane: 'watch',
    split: null,
  },
];

export const controlGroup = (id: string | null | undefined): ControlGroupDef | undefined =>
  CONTROL_GROUPS.find((g) => g.id === id);

/** The maintenance kinds, their labels and tones (ST-00249 adds planned and breakdown). */
export const MAINTENANCE_KINDS: Record<
  MaintenanceKind,
  { label: string; tone: AssetStatusKey; order: number }
> = {
  breakdown: { label: 'Breakdown', tone: 'breakdown', order: 0 },
  planned: { label: 'Planned maintenance', tone: 'planned_maintenance', order: 1 },
  under_maintenance: { label: 'Under maintenance', tone: 'maintenance', order: 2 },
  out_of_service: { label: 'Out of service', tone: 'maintenance', order: 3 },
};
export const BreakdownIcon = FaCarCrash;

/**
 * The control room's look per status key: the group panel, its count, the icon badge, the thin
 * progress bar and the chip's accent text — the shared scheme's classes (map/assetStatus), whose
 * tokens flip with the theme (also on the wall display's own data-theme).
 */
export type ControlToneStyle = AssetStatusClasses;
export const CONTROL_TONE_STYLES: Record<AssetStatusKey, ControlToneStyle> = ASSET_STATUS_CLASSES;

/** The marker colour of a placement's key (the chip's asset icon, the maps). */
export const controlToneMarker = (tone: AssetStatusKey): string => ASSET_STATUS[tone].marker;

// ── Time ────────────────────────────────────────────────────────────────────

/** The engine's OFFLINE threshold (STATUS_DEFAULTS.offlineAfterS): 90 minutes without data. */
export const OFFLINE_AFTER_S = 90 * 60;

/** ms of an ISO instant, or null. */
const msOf = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : null;
};

/** Seconds from `iso` to `now` (never negative), or null. */
export const elapsedS = (iso: string | null | undefined, now: number): number | null => {
  const t = msOf(iso);
  return t === null ? null : Math.max(0, Math.floor((now - t) / 1000));
};

/**
 * Time in a state, readable from across a room: "35s", "12m", "3h 12m", "2d 4h". `compact`
 * (the desktop chips, as the board's own fmtDuration): "3h12". `seconds` (the live-ticking
 * maintenance clock): "12m 05s", "3h 12m 05s", "2d 4h 12m".
 */
export function fmtElapsed(
  seconds: number | null | undefined,
  { seconds: withSeconds = false, compact = false }: { seconds?: boolean; compact?: boolean } = {}
): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return '—';
  const s = Math.max(0, Math.floor(seconds));
  const pad = (n: number) => String(n).padStart(2, '0');
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return withSeconds ? `${m}m ${pad(s % 60)}s` : `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 48) {
    if (withSeconds) return `${h}h ${pad(m % 60)}m ${pad(s % 60)}s`;
    return compact ? `${h}h${pad(m % 60)}` : `${h}h ${pad(m % 60)}m`;
  }
  return withSeconds
    ? `${Math.floor(h / 24)}d ${h % 24}h ${pad(m % 60)}m`
    : `${Math.floor(h / 24)}d ${h % 24}h`;
}

// ── Reading a row ───────────────────────────────────────────────────────────

/** Data age: the device time of the latest position, else when the engine last saw it. */
const updatedAt = (row: StatusBoardRow): string | null =>
  row.position?.ts || row.last_update || null;

/** The context in force: a manual override's mode wins over the engine's context. */
export const effectiveContext = (row: StatusBoardRow): string =>
  row.status?.override?.mode || row.status?.context || 'UNALLOCATED';

const MAINTENANCE_CONTEXTS = ['UNDER_MAINTENANCE', 'OUT_OF_SERVICE'];

/** The stage → context the engine derives from an active trip (engine trip.ts tripContextOf). */
const STAGE_CONTEXT: Record<string, string> = {
  EN_ROUTE_TO_SOURCE: 'EN_ROUTE_TO_SOURCE',
  AT_SOURCE: 'AT_SOURCE',
  IN_TRANSIT_LOADED: 'IN_TRANSIT_LOADED',
  AT_DESTINATION: 'AT_DESTINATION',
  IN_TRANSIT_EMPTY: 'IN_TRANSIT_EMPTY',
};
const BEFORE_LOADED = ['DISPATCHED', 'ACKNOWLEDGED', 'EN_ROUTE_TO_SOURCE', 'AT_SOURCE'];
const NOT_MOVED_YET = ['DISPATCHED', 'ACKNOWLEDGED'];

/** Moving, by the engine's motion; without one, by the last position's speed. */
function isMoving(row: StatusBoardRow): boolean {
  const motion = row.status?.motion;
  if (motion) return motion === 'MOVING' || motion === 'TOWED';
  const speed = row.position?.speed_kmh;
  return speed !== null && speed !== undefined && Number(speed) >= 5;
}

/** Geofence purposes that only inform (never "where a truck is parked") unless nothing else. */
const INFO_ONLY_PURPOSES = ['speed_zone', 'risk_zone', 'time_and_motion', 'no_go'];

/** The zone a truck is standing in: the newest-entered meaningful one (zones come newest first). */
export function primaryZone(
  zones: BoardZone[] | null | undefined,
  purposes?: string[]
): BoardZone | null {
  const list = zones || [];
  if (purposes) return list.find((z) => purposes.includes(String(z.purpose_type))) || null;
  return list.find((z) => !INFO_ONLY_PURPOSES.includes(String(z.purpose_type))) || list[0] || null;
}

export const zoneLabel = (z: BoardZone): string => z.name || `Geofence ${z.geofence_id}`;

// ── Placement ───────────────────────────────────────────────────────────────

export interface ControlSub {
  /** Stable within its group: site:<id>, zone:<id>, outside, stopped, kind:<kind>, … */
  key: string;
  label: string;
  /** A sub-group with its own status key (Breakdown). */
  tone?: AssetStatusKey;
  /** Sort order within the group (lower first); ties by label. */
  order?: number;
  /** The label is a geofence's name standing in for the site's (a trip's site name wins). */
  weak?: boolean;
}

export interface Placement {
  group: ControlGroupId;
  sub: ControlSub | null;
  /** When the truck went into this state, as well as the board knows. */
  since: string | null;
  /** false: `since` is a lower bound (the status's own start) — "at least this long". */
  sinceExact: boolean;
  /** A short qualifier shown on the chip ("stopped en route", "at weighbridge", …). */
  note: string | null;
  /** The truck's status key (the group's, or its sub-group's) — its colour on every screen. */
  tone: AssetStatusKey;
  /** Data age in seconds (null: no data at all). */
  ageS: number | null;
  /** The rules' visual for the truck (map/assetVisualRules): key, label, overlays. */
  visual: AssetVisual;
  /** ST-00276: the server's display of the truck (the organisation's rules), when it sent one. */
  display: AssetDisplay | null;
}

const siteSub = (id: number | null | undefined, name: string | null | undefined): ControlSub =>
  id !== null && id !== undefined
    ? { key: `site:${id}`, label: name || `Site ${id}` }
    : name
      ? { key: `site:${name}`, label: name }
      : { key: 'site:unknown', label: 'Site not known', order: 9 };

/** The site of a truck at its source / destination: the trip's, else the zone it is in. */
function siteOf(row: StatusBoardRow, side: 'source' | 'destination'): ControlSub {
  const trip = row.trip;
  if (trip) {
    const id = side === 'source' ? trip.from_location_id : trip.to_location_id;
    const name = side === 'source' ? trip.from_name : trip.to_name;
    if (id != null || name) return siteSub(id, name);
  }
  const zone =
    primaryZone(
      row.zones,
      side === 'source' ? ['source', 'site'] : ['destination', 'tipping_zone', 'site']
    ) || primaryZone(row.zones);
  if (zone) return { ...siteSub(zone.location_id, zone.name), weak: true };
  return siteSub(null, null);
}

/** The board's best start of the current stage / status. */
const stageOrStatusSince = (row: StatusBoardRow, stages: string[]): string | null =>
  (row.trip && stages.includes(String(row.trip.stage)) && row.trip.stage_since) ||
  row.status?.since ||
  null;

/** Where a truck goes on the control room, and since when. Pure. */
export function placeRow(row: StatusBoardRow, now: number): Placement {
  const ageS = elapsedS(updatedAt(row), now);
  const statusSince = row.status?.since || null;
  const visual = visualOfRow(row, now);
  const place = (
    group: ControlGroupId,
    rest: Partial<Omit<Placement, 'group' | 'ageS'>> = {}
  ): Placement => ({
    group,
    sub: rest.sub ?? null,
    since: rest.since === undefined ? statusSince : rest.since,
    sinceExact: rest.sinceExact ?? true,
    note: rest.note ?? null,
    // The truck's colour: the rules' key (the default rules give the group's / sub-group's).
    tone: visual.key,
    visual,
    display: row.display ?? null,
    ageS,
  });

  const ctx = effectiveContext(row);

  // 1. Maintenance (declared: holds while the tracker is silent).
  const m = row.maintenance;
  if (m || MAINTENANCE_CONTEXTS.includes(ctx)) {
    const kind: MaintenanceKind =
      m?.kind || (ctx === 'OUT_OF_SERVICE' ? 'out_of_service' : 'under_maintenance');
    const meta = MAINTENANCE_KINDS[kind] || MAINTENANCE_KINDS.under_maintenance;
    const override = row.status?.override;
    const since = m
      ? m.since
      : override && MAINTENANCE_CONTEXTS.includes(String(override.mode)) && override.set_at
        ? override.set_at
        : statusSince;
    const exact = m ? m.since_exact : !!(override && override.set_at);
    return place('maintenance', {
      sub: { key: `kind:${kind}`, label: meta.label, tone: meta.tone, order: meta.order },
      since,
      sinceExact: exact,
      note: m?.zone?.name ? `in ${m.zone.name}` : m?.overdue ? 'past its expected end' : null,
      tone: meta.tone,
    });
  }

  // 1b. A declared delay (an open delay event): Delayed, per code.
  const d = row.delay;
  if (d) {
    return place('delayed', {
      sub: { key: `event:${d.event_code || 'delay'}`, label: d.label || 'Delay', order: 1 },
      since: d.since,
      sinceExact: !!d.since,
      note: d.overdue ? 'past its expected end' : null,
    });
  }

  // 2. Offline / no data.
  if (ageS === null) {
    return place('offline', {
      sub: { key: 'never', label: 'No data yet', order: 1 },
      since: null,
      sinceExact: false,
    });
  }
  if (isOffline(row.status) || ageS >= OFFLINE_AFTER_S) {
    return place('offline', {
      sub: { key: 'stale', label: 'Stopped reporting', order: 0 },
      since: updatedAt(row),
      note: 'last seen',
    });
  }

  const stage = row.trip?.stage || null;
  // A context the engine has not derived yet: the active trip's stage, as the engine ranks it.
  const context =
    (ctx === 'UNALLOCATED' || !row.status?.context) &&
    !row.status?.override?.mode &&
    stage &&
    STAGE_CONTEXT[stage]
      ? (STAGE_CONTEXT[stage] as string)
      : ctx;
  const motion = row.status?.motion || null;

  // 3. Delayed.
  if (context === 'IN_DELAY_ZONE') {
    const zone = primaryZone(row.zones, ['delay', 'border_post']);
    return place('delayed', {
      sub: zone
        ? { key: `zone:${zone.geofence_id}`, label: zoneLabel(zone) }
        : { key: 'delay_zone', label: 'Delay zone' },
      since: zone?.entered_at || statusSince,
      sinceExact: !!zone?.entered_at,
    });
  }
  if (TRANSIT_CONTEXTS.includes(context) && (motion === 'STOPPED' || motion === 'PARKED')) {
    return place('delayed', {
      sub: { key: 'stopped', label: 'Stopped en route', order: 0 },
      since: statusSince,
      note: context === 'IN_TRANSIT_LOADED' ? 'stopped, loaded' : 'stopped, empty',
    });
  }

  // 4. The load cycle.
  const idling = motion === 'IDLING' ? 'idling' : null;
  if (context === 'AT_WEIGHBRIDGE') {
    const sourceSide = !stage || BEFORE_LOADED.includes(stage);
    return place(sourceSide ? 'at_source' : 'at_destination', {
      sub: siteOf(row, sourceSide ? 'source' : 'destination'),
      since: statusSince,
      note: 'at weighbridge',
    });
  }
  switch (context) {
    case 'EN_ROUTE_TO_SOURCE':
      return place('to_source', {
        since: stageOrStatusSince(row, ['EN_ROUTE_TO_SOURCE']),
        note: idling,
      });
    case 'AT_SOURCE':
      return place('at_source', {
        sub: siteOf(row, 'source'),
        since: stageOrStatusSince(row, ['AT_SOURCE']),
        note: idling,
      });
    case 'IN_TRANSIT_LOADED':
      return place('loaded', {
        since: stageOrStatusSince(row, ['IN_TRANSIT_LOADED']),
        note: idling,
      });
    case 'AT_DESTINATION':
      return place('at_destination', {
        sub: siteOf(row, 'destination'),
        since: stageOrStatusSince(row, ['AT_DESTINATION']),
        note: idling,
      });
    case 'IN_TRANSIT_EMPTY':
      return place('empty', { since: stageOrStatusSince(row, ['IN_TRANSIT_EMPTY']), note: idling });
    default:
      break;
  }
  if (stage && NOT_MOVED_YET.includes(stage)) {
    return place('to_source', {
      since: row.trip?.stage_since || row.trip?.dispatched_at || statusSince,
      // ST-00205's tag says "waiting for first report" on the chip; the note covers the rest.
      note: row.trip?.awaiting_first_report ? null : 'not moving yet',
    });
  }

  // 5. Parked / 6. Unallocated.
  if (context === 'AT_DEPOT' || context === 'STANDBY' || !isMoving(row)) {
    const zone = primaryZone(row.zones);
    return place('parked', {
      sub: zone
        ? { key: `zone:${zone.geofence_id}`, label: zoneLabel(zone) }
        : { key: 'outside', label: 'Outside any zone', order: 9 },
      note: context === 'STANDBY' ? 'standby' : idling,
    });
  }
  return place('unallocated');
}

/**
 * THE status key of a status board row (ST-00253 / ST-00244): its control room placement. Every
 * surface that has the row — the control room, the table, Kanban and phone cards, Live View —
 * colours the truck with this key, so it has the same colour everywhere at the same moment.
 */
export const statusKeyOfRow = (row: StatusBoardRow, now: number = Date.now()): AssetStatusKey =>
  visualOfRow(row, now).key;

/**
 * ST-00276: a row's visual — the server's display (the organisation's display rules) when the row
 * carries one, else the default rules over the row (map/assetVisualRules).
 */
export const visualOfRow = (row: StatusBoardRow, now: number = Date.now()): AssetVisual =>
  row.display ? visualOfDisplay(row.display) : resolveAssetVisual(assetStateOfRow(row, now));

/**
 * A status board row as the rules read it (map/assetVisualRules AssetState): the context in
 * force (a manual override wins; the active trip's stage stands in while the engine has not
 * derived one), the trip stage, motion (the engine's, else the last speed), offline (no data,
 * the OFFLINE overlay, or 90 minutes old), the maintenance block's kind, an open delay event
 * (row.delay, the maintenance module) and the flags.
 */
export function assetStateOfRow(row: StatusBoardRow, now: number): AssetState {
  const ageS = elapsedS(updatedAt(row), now);
  const ctx = effectiveContext(row);
  const stage = row.trip?.stage || null;
  const context =
    (ctx === 'UNALLOCATED' || !row.status?.context) &&
    !row.status?.override?.mode &&
    stage &&
    RULE_STAGE_CONTEXT[stage]
      ? (RULE_STAGE_CONTEXT[stage] as string)
      : ctx;
  const overlays = row.status?.overlays || [];
  const flags: AssetFlag[] = [];
  if (row.trip?.stage === 'DISPATCHED' && (Number(row.trip?.ack_reminders) || 0) > 0)
    flags.push('driver_reminded');
  if (row.trip?.awaiting_first_report) flags.push('awaiting_first_report');
  if (overlays.includes('ALERT_OPEN')) flags.push('alert_open');
  if (overlays.includes('DEVIATING')) flags.push('deviating');
  if (overlays.includes('NO_DRIVER')) flags.push('no_driver');
  const offline = ageS === null || isOffline(row.status) || ageS >= OFFLINE_AFTER_S;
  return {
    context,
    stage,
    motion: normaliseMotion(row.status?.motion) ?? (isMoving(row) ? 'moving' : null),
    offline,
    stale: offline,
    maintenance: row.maintenance?.kind ?? null,
    // ST-00249: an open delay event (row.delay) — Delayed, also while the tracker is silent.
    delayed: !!row.delay,
    flags,
  };
}

// ── Scope (Fleet / Loaders, site, route, customer) ──────────────────────────

export type ControlClass = 'all' | 'fleet' | 'loaders';

export interface ControlScope {
  cls: ControlClass;
  /** location_id as a string ('' = any). */
  site: string;
  /** route id ('' = any). */
  route: string;
  /** the load's consignor ('' = any). */
  customer: string;
}

export const EMPTY_SCOPE: ControlScope = { cls: 'all', site: '', route: '', customer: '' };

/** Scope ↔ URL (cls, site, route, cust). */
export function scopeFromParams(params: URLSearchParams): ControlScope {
  const cls = params.get('cls');
  return {
    cls: cls === 'fleet' || cls === 'loaders' ? cls : 'all',
    site: params.get('site') || '',
    route: params.get('route') || '',
    customer: params.get('cust') || '',
  };
}
export const scopeToParams = (scope: ControlScope): Record<string, string> => ({
  cls: scope.cls === 'all' ? '' : scope.cls,
  site: scope.site,
  route: scope.route,
  cust: scope.customer,
});
export const scopeIsSet = (s: ControlScope): boolean =>
  s.cls !== 'all' || !!s.site || !!s.route || !!s.customer;

export const isLoader = (row: StatusBoardRow): boolean =>
  assetMarkerKind(
    row.asset as { asset_type?: string | null; vehicle_class?: string | null } | null
  ) === 'loader';

const consignorOf = (row: StatusBoardRow): string | null => {
  const c = row.trip?.load?.consignor;
  return typeof c === 'string' && c.trim() ? c.trim() : null;
};

/** The sites a row touches: its trip's two ends and the sites of the zones it is in. */
const siteIdsOf = (row: StatusBoardRow): string[] =>
  [
    row.trip?.from_location_id,
    row.trip?.to_location_id,
    ...(row.zones || []).map((z) => z.location_id),
  ]
    .filter((v) => v !== null && v !== undefined)
    .map(String);

export function matchesScope(row: StatusBoardRow, scope: ControlScope): boolean {
  if (scope.cls === 'fleet' && isLoader(row)) return false;
  if (scope.cls === 'loaders' && !isLoader(row)) return false;
  if (scope.site && !siteIdsOf(row).includes(scope.site)) return false;
  if (scope.route && String(row.trip?.route?.id || '') !== scope.route) return false;
  if (scope.customer && consignorOf(row) !== scope.customer) return false;
  return true;
}

export interface ScopeOption {
  value: string;
  label: string;
}

/** The sites, routes and customers the board's rows carry (for the scope pickers). */
export function scopeOptions(rows: StatusBoardRow[]): {
  sites: ScopeOption[];
  routes: ScopeOption[];
  customers: ScopeOption[];
  loaders: number;
} {
  const sites = new Map<string, string>();
  const zoneSites = new Map<string, string>();
  const routes = new Map<string, string>();
  const customers = new Set<string>();
  let loaders = 0;
  for (const r of rows) {
    if (isLoader(r)) loaders++;
    const t = r.trip;
    if (t) {
      if (t.from_location_id != null)
        sites.set(String(t.from_location_id), t.from_name || `Site ${t.from_location_id}`);
      if (t.to_location_id != null)
        sites.set(String(t.to_location_id), t.to_name || `Site ${t.to_location_id}`);
      if (t.route?.id)
        routes.set(
          String(t.route.id),
          [t.route.code, t.route.name].filter(Boolean).join(' · ') || 'Route'
        );
    }
    for (const z of r.zones || []) {
      if (z.location_id != null)
        zoneSites.set(String(z.location_id), z.name || `Site ${z.location_id}`);
    }
    const c = consignorOf(r);
    if (c) customers.add(c);
  }
  for (const [id, name] of zoneSites) if (!sites.has(id)) sites.set(id, name);
  const sorted = (m: Map<string, string>): ScopeOption[] =>
    [...m]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  return {
    sites: sorted(sites),
    routes: sorted(routes),
    customers: [...customers]
      .sort((a, b) => a.localeCompare(b))
      .map((c) => ({ value: c, label: c })),
    loaders,
  };
}

// ── The board ───────────────────────────────────────────────────────────────

export interface PlacedRow {
  row: StatusBoardRow;
  place: Placement;
  /** Seconds in the state now (null: not known). */
  inStateS: number | null;
}

export interface ControlSubGroup extends ControlSub {
  rows: PlacedRow[];
}

export interface ControlGroupView {
  def: ControlGroupDef;
  rows: PlacedRow[];
  subs: ControlSubGroup[];
  /** The longest time in state in the group (seconds), for the header. */
  longestS: number | null;
}

export interface ControlTotals {
  fleet: number;
  working: number;
  idle: number;
  maintenance: number;
  offline: number;
  delayed: number;
}

export interface ControlBoard {
  groups: ControlGroupView[];
  byId: Record<ControlGroupId, ControlGroupView>;
  totals: ControlTotals;
}

/** Longest in state first; unknown durations last; then by registration. */
const byTimeInState = (a: PlacedRow, b: PlacedRow): number =>
  (b.inStateS ?? -1) - (a.inStateS ?? -1) ||
  String(a.row.registration || a.row.asset_key).localeCompare(
    String(b.row.registration || b.row.asset_key)
  );

/** Every row placed and grouped, groups in CONTROL_GROUPS order, subs largest first. Pure. */
export function buildControlBoard(rows: StatusBoardRow[], now: number): ControlBoard {
  const byId = {} as Record<ControlGroupId, ControlGroupView>;
  for (const def of CONTROL_GROUPS) byId[def.id] = { def, rows: [], subs: [], longestS: null };
  for (const row of rows) {
    const place = placeRow(row, now);
    byId[place.group].rows.push({ row, place, inStateS: elapsedS(place.since, now) });
  }
  const totals: ControlTotals = {
    fleet: rows.length,
    working: 0,
    idle: 0,
    maintenance: 0,
    offline: 0,
    delayed: 0,
  };
  for (const def of CONTROL_GROUPS) {
    const g = byId[def.id];
    g.rows.sort(byTimeInState);
    g.longestS = g.rows.reduce<number | null>(
      (m, r) => (r.inStateS !== null && (m === null || r.inStateS > m) ? r.inStateS : m),
      null
    );
    if (def.split) {
      const subs = new Map<string, ControlSubGroup>();
      for (const pr of g.rows) {
        const sub = pr.place.sub || { key: 'other', label: 'Other', order: 9 };
        const known = subs.get(sub.key);
        if (!known) subs.set(sub.key, { ...sub, rows: [pr] });
        else {
          // A trip's site name names the sub-group over a geofence's standing in for it.
          if (known.weak && !sub.weak) Object.assign(known, { label: sub.label, weak: false });
          known.rows.push(pr);
        }
      }
      g.subs = [...subs.values()].sort(
        (a, b) =>
          (a.order ?? 5) - (b.order ?? 5) ||
          b.rows.length - a.rows.length ||
          a.label.localeCompare(b.label)
      );
    }
    totals[def.bucket] += g.rows.length;
    if (def.id === 'delayed') totals.delayed = g.rows.length;
  }
  return { groups: CONTROL_GROUPS.map((d) => byId[d.id]), byId, totals };
}

// ── Drill-through (the table's f=room.<group>[.<sub>]) ──────────────────────

export const ROOM_FILTER_PREFIX = 'room.';

export const roomFilterId = (group: ControlGroupId, subKey?: string | null): string =>
  `${ROOM_FILTER_PREFIX}${group}${subKey ? `.${subKey}` : ''}`;

/** f=room.<group>[.<sub>] → { group, sub }, else null. */
export function parseRoomFilter(
  f: string | null | undefined
): { group: ControlGroupId; sub: string | null } | null {
  if (!f || !f.startsWith(ROOM_FILTER_PREFIX)) return null;
  const rest = f.slice(ROOM_FILTER_PREFIX.length);
  const dot = rest.indexOf('.');
  const group = dot === -1 ? rest : rest.slice(0, dot);
  if (!controlGroup(group)) return null;
  return { group: group as ControlGroupId, sub: dot === -1 ? null : rest.slice(dot + 1) || null };
}

/** The row predicate of a room filter (clock read when the filter runs). */
export function roomFilterMatcher(
  f: string,
  now: () => number = Date.now
): ((r: StatusBoardRow) => boolean) | null {
  const parsed = parseRoomFilter(f);
  if (!parsed) return null;
  return (r) => {
    const p = placeRow(r, now());
    return p.group === parsed.group && (!parsed.sub || p.sub?.key === parsed.sub);
  };
}

/** "Parked · Home base" — the table's chip for a room filter. */
export function roomFilterLabel(f: string, rows: StatusBoardRow[], now: number): string | null {
  const parsed = parseRoomFilter(f);
  if (!parsed) return null;
  const def = controlGroup(parsed.group) as ControlGroupDef;
  if (!parsed.sub) return def.label;
  const hits = rows
    .map((r) => placeRow(r, now))
    .filter((p) => p.group === parsed.group && p.sub?.key === parsed.sub);
  const hit = hits.find((p) => !p.sub?.weak) || hits[0];
  return `${def.label} · ${hit?.sub?.label || parsed.sub.replace(/^[a-z_]+:/, '')}`;
}
